/**
 * lib/dates.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Every date and time string the product renders. The design uses exactly
 * three forms and no others, so this module exposes exactly three formatters:
 *
 *   date      "24 Aug 2026"                 — expiries, last days, periods
 *   dateTime  "24 Aug 2026 · 09:40"         — ledger rows, count footers
 *   stamp     "Balances as of 24 Aug 2026 · 09:40 GST" — the header clock
 *
 * Formatting is done by hand rather than with `Intl` because the output must
 * be byte-identical on Android, iOS and every browser locale: a console whose
 * timestamps read "8/24/2026" for one user and "24/08/2026" for another is a
 * console where two people cannot discuss the same row.
 * ──────────────────────────────────────────────────────────────────────────
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Accepts anything the API can return for a timestamp; null when unparseable. */
export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** "24 Aug 2026" */
export function date(value: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "09:40" — 24-hour, because a subledger has no room for am/pm ambiguity. */
export function time(value: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "24 Aug 2026 · 09:40" — the separator is the design's middle dot. */
export function dateTime(value: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${date(d)} · ${time(d)}`;
}

/**
 * The header clock: "24 Aug 2026 · 09:40 GST". Every balance in this product
 * states its as-of time — a cash position without one is a claim nobody can
 * check. The zone abbreviation comes from the runtime, since an org in Dubai
 * and one in Bangalore must each see their own.
 */
export function stamp(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return `${dateTime(d)} ${zoneAbbreviation(d)}`;
}

/**
 * Short zone label. `Intl` is used ONLY here, where a locale-specific answer
 * is the correct answer, and it falls back to a UTC offset when the runtime
 * has no time-zone database (some Android builds ship without one).
 */
export function zoneAbbreviation(d: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' }).formatToParts(d);
    const zone = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (zone) return zone;
  } catch {
    // fall through to the offset form
  }
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** Whole days between a timestamp and now. Null when there is no timestamp. */
export function daysSince(value: string | number | Date | null | undefined, now = new Date()): number | null {
  const d = toDate(value);
  if (!d) return null;
  return Math.floor((now.getTime() - d.getTime()) / 86_400_000);
}

/**
 * The register's "Reconciled" column: "55d ago", "4d ago", or "never" —
 * matching the design, which renders `days + 'd ago'` whenever there is a last
 * count and `never` when there is not. There is deliberately no "today" case:
 * the design shows "0d ago", and inventing a friendlier word for it would make
 * this column read differently from the reference.
 *
 * The colour that goes with it is decided by `staleness()` in lib/status.ts —
 * this returns the label only, so the two never disagree about the number.
 */
export function ageLabel(value: string | number | Date | null | undefined, now = new Date()): string {
  const days = daysSince(value, now);
  if (days === null) return 'never';
  return `${Math.max(0, days)}d ago`;
}

/** "oldest 15 Aug 2026 · 10:22" — the Overview's pending-approvals sub line. */
export function oldestLabel(value: string | number | Date | null | undefined): string | null {
  const d = toDate(value);
  return d ? `oldest ${dateTime(d)}` : null;
}
